import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { useSiteContent } from '@/context/SiteContentContext';
import AdminHeroTab from './components/AdminHeroTab';
import AdminAboutTab from './components/AdminAboutTab';
import AdminServicesTab from './components/AdminServicesTab';
import AdminTeamTab from './components/AdminTeamTab';
import AdminContactTab from './components/AdminContactTab';
import AdminProjectsTab from './components/AdminProjectsTab';
import AdminSettingsTab from './components/AdminSettingsTab';

const tabs = [
  { id: 'hero', label: 'Hero', icon: 'ri-home-4-line' },
  { id: 'about', label: 'About', icon: 'ri-information-line' },
  { id: 'services', label: 'Services', icon: 'ri-briefcase-line' },
  { id: 'team', label: 'Team', icon: 'ri-team-line' },
  { id: 'projects', label: 'Projects', icon: 'ri-gallery-line' },
  { id: 'contact', label: 'Contact & Footer', icon: 'ri-contacts-line' },
  { id: 'settings', label: 'Settings', icon: 'ri-settings-3-line' },
];

export default function AdminPage() {
  const { isAdmin, logout, currentUsername } = useAdminAuth();
  const { resetContent } = useSiteContent();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('hero');
  const [saved, setSaved] = useState(false);
  const [showReset, setShowReset] = useState(false);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
            <i className="ri-lock-line text-red-400 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500 text-sm mb-6">You need to log in as admin first.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors text-sm"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    resetContent();
    setShowReset(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Bar */}
      <header className="bg-teal-950 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-700">
            <i className="ri-settings-3-line text-teal-200 text-lg"></i>
          </div>
          <div>
            <div className="font-bold text-sm">GeoEng Admin Panel</div>
            <div className="text-teal-400 text-xs">Logged in as {currentUsername}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-teal-300 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line"></i>
            View Site
          </button>
          <button
            onClick={() => setShowReset(true)}
            className="flex items-center gap-1.5 text-red-400 hover:text-red-300 text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Reset All
          </button>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="flex items-center gap-1.5 bg-teal-800 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-logout-box-line"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-gray-100 flex flex-col py-6 shrink-0">
          <nav className="flex flex-col gap-1 px-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap text-left ${
                  activeTab === tab.id
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${tab.icon} text-base`}></i>
                </div>
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto px-3">
            <button
              onClick={handleSave}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${
                saved
                  ? 'bg-green-500 text-white'
                  : 'bg-teal-600 hover:bg-teal-700 text-white'
              }`}
            >
              <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
              {saved ? 'Saved!' : 'Save Changes'}
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">Changes save automatically</p>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-8">
          <div className="max-w-3xl mx-auto">
            {activeTab === 'hero' && <AdminHeroTab />}
            {activeTab === 'about' && <AdminAboutTab />}
            {activeTab === 'services' && <AdminServicesTab />}
            {activeTab === 'team' && <AdminTeamTab />}
            {activeTab === 'projects' && <AdminProjectsTab />}
            {activeTab === 'contact' && <AdminContactTab />}
            {activeTab === 'settings' && <AdminSettingsTab />}
          </div>
        </main>
      </div>

      {/* Reset Confirm Modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
              <i className="ri-alert-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Reset All Content?</h3>
            <p className="text-gray-500 text-sm text-center mb-6">This will restore all website content to its original defaults. This cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReset(false)}
                className="flex-1 border border-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors text-sm"
              >
                Yes, Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
